import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../components/Spinner";

const PRODUCT_API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const INVENTORY_API_BASE = process.env.REACT_APP_INVENTORY_SERVICE_URL;
export default function CatalogPage() {
  const { auth } = useAuth();
  const sellerId = auth?.userId;
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [inventories, setInventories] = useState({});
  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalPages: 0,
  });
  const [selected, setSelected] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [serverSearch, setServerSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(
    async (page = 0, size = 10, searchTerm = "") => {
      try {
        setLoading(true);

        const res = await fetch(
          `${PRODUCT_API_BASE}/products/sellers/${sellerId}?page=${page}&size=${size}&search=${searchTerm}&sortBy=createdAt&direction=DESC`,
        );

        if (!res.ok) throw new Error("Failed to fetch products");

        const data = await res.json();

        setProducts(data.content || []);
        setPageInfo({
          pageNumber: data.pageNumber,
          pageSize: data.pageSize,
          totalPages: data.totalPages,
        });
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    },
    [sellerId],
  );
  const loadInventory = useCallback(async (productId) => {
    const res = await fetch(
      `${INVENTORY_API_BASE}/inventories/stock?productId=${productId}`,
    );
    const data = await res.json();
    setInventories((prev) => ({ ...prev, [productId]: data }));
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadProducts(0, pageInfo.pageSize, serverSearch);
    }, 400);

    return () => clearTimeout(timeout);
  }, [serverSearch]);

  useEffect(() => {
    products.forEach((p) => loadInventory(p.id));
  }, [products, loadInventory]);

  const filtered = products.filter(
    (p) =>
      p.productName.toLowerCase().includes(search.toLowerCase()) ||
      p.sku.toLowerCase().includes(search.toLowerCase()),
  );

  const updateStatus = async (productId, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this product?`))
      return;

    try {
      setActionLoading(productId);

      await fetch(`${PRODUCT_API_BASE}/products/${productId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, status } : p)),
      );
    } catch (err) {
      console.error("Status update failed", err);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Delete this product permanently?")) return;

    try {
      setActionLoading(productId);

      await fetch(`${PRODUCT_API_BASE}/products/${productId}`, {
        method: "DELETE",
      });

      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 fw-semibold">Catalog</h4>
        <Link
          to="/dashboard/catalog/products/new"
          className="btn btn-link fw-semibold text-decoration-underline"
        >
          Add products
        </Link>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Name or SKU..."
          value={serverSearch}
          onChange={(e) => setServerSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="table-responsive border rounded">
        {loading ? (
          <Spinner />
        ) : (
          <table className="table mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>SKU</th>
                <th>Title</th>
                <th>Image</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const inv = inventories[p.id]; // inventory response for this product
                return (
                  <tr key={p.id}>
                    <td>{p.sku}</td>
                    <td>{p.productName}</td>
                    <td>
                      {p.mainImageUrl ? (
                        <img
                          src={`https://auth.afrogebeya.com/uploads/products/${p.mainImageUrl}`}
                          alt={p.productName}
                          style={{ width: 40, height: 40, borderRadius: 4 }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            background: "#f1f1f1",
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </td>
                    <td>
                      {p.price} {p.currency}
                    </td>
                    {/* Inventory values */}
                    <td>
                      {inv ? (
                        <>
                          <div>Sellable: {inv.sellableQuantity}</div>
                          <div>Total: {inv.totalPhysicalQuantity}</div>
                        </>
                      ) : (
                        <Spinner />
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          p.moderationStatus === "APPROVED"
                            ? "bg-success"
                            : p.moderationStatus === "PENDING"
                              ? "bg-warning text-dark"
                              : p.moderationStatus === "REJECTED"
                                ? "bg-danger"
                                : "bg-secondary"
                        }`}
                      >
                        {p.moderationStatus}
                      </span>
                    </td>
                    <td>
                      <div className="dropdown">
                        <button
                          className="btn btn-sm btn-light dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          Actions
                        </button>

                        <ul className="dropdown-menu">
                          <li>
                            <Link
                              className="dropdown-item"
                              to={`/dashboard/catalog/products/${p.id}/edit`}
                            >
                              Edit
                            </Link>
                          </li>

                          {p.status !== "SUSPENDED" && (
                            <li>
                              <button
                                className="dropdown-item text-warning"
                                onClick={() => updateStatus(p.id, "SUSPENDED")}
                              >
                                Suspend
                              </button>
                            </li>
                          )}

                          {p.status === "SUSPENDED" && (
                            <li>
                              <button
                                className="dropdown-item text-success"
                                onClick={() => updateStatus(p.id, "ACTIVE")}
                              >
                                Activate
                              </button>
                            </li>
                          )}

                          <li>
                            <button
                              className="dropdown-item text-danger"
                              onClick={() => deleteProduct(p.id)}
                            >
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <button
          className="btn btn-outline-primary"
          disabled={pageInfo.pageNumber === 0}
          onClick={() =>
            loadProducts(pageInfo.pageNumber - 1, pageInfo.pageSize)
          }
        >
          ← Previous
        </button>
        <span>
          Page {pageInfo.pageNumber + 1} of {pageInfo.totalPages}
        </span>
        <button
          className="btn btn-outline-primary"
          disabled={pageInfo.pageNumber + 1 >= pageInfo.totalPages}
          onClick={() =>
            loadProducts(pageInfo.pageNumber + 1, pageInfo.pageSize)
          }
        >
          Next →
        </button>
      </div>
    </div>
  );
}
